import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { PasswordResetControlsComponent } from '../password-reset-controls/password-reset-controls.component';
import { OnscreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';
import { authenticationRoutes } from '../authentication-routes';
import { ResetPasswordService } from './reset-password.service';
import { ActiveAuthenticationStateService } from '@auth-n/state/active-authentication.state-service';
import { LoggingService } from '@logging/logging.service';
import { H4Component } from '@design/typography/h4/h4.component';
import { InputComponent } from '@design/forms/input/input.component';
import { ButtonComponent } from '@design/buttons/button/button.component';
import { TestIdDirective } from '@design/core/test-id.directive';
import { AutoFocusDirective } from '@design/forms/app-auto-focus.directive';

@Component({
  selector: 'app-reset-password-form',
  templateUrl: './reset-password-form.component.html',
  styleUrls: ['./reset-password-form.component.scss'],
  providers: [ResetPasswordService],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    H4Component,
    InputComponent,
    ButtonComponent,
    PasswordResetControlsComponent,
    TestIdDirective,
    AutoFocusDirective
  ]
})
export class ResetPasswordFormComponent {
  inFlight$ = this.authenticationState.inFlight$;

  emailControl = new UntypedFormControl('', [Validators.required, Validators.email]);
  resetPasswordRequestFormGroup: UntypedFormGroup = new UntypedFormGroup({
    email: this.emailControl
  });

  token = this.route.snapshot.queryParams['t'];

  currentStep: 'RequestToken' | 'CompleteResetPassword' = !this.token ? 'RequestToken' : 'CompleteResetPassword';

  resetPasswordFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  @ViewChild('passwordResetControls') passwordResetControls: PasswordResetControlsComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private messagingService: OnscreenMessagingService,
    private resetPasswordService: ResetPasswordService,
    private authenticationState: ActiveAuthenticationStateService,
    private logger: LoggingService
  ) {
    logger.trace('ResetPasswordFormComponent - token: ', this.token);
  }

  requestResetPassword() {
    if (!this.resetPasswordRequestFormGroup.valid) return;

    const email = this.emailControl.value.toLowerCase();
    this.resetPasswordService.requestResetPassword$(email).subscribe(
      (_) => {
        this.router
          .navigateByUrl(authenticationRoutes.signIn, {
            state: {
              message: {
                title: 'Please check your email for instructions on how to regain access to your account.'
              }
            }
          })
          .then();
      },
      (_) => {
        this.messagingService.warn('An unexpected error occurred.');
      }
    );
  }

  completeResetPassword() {
    if (!this.resetPasswordFormGroup.valid) return;

    this.resetPasswordService
      .resetPassword$(this.token, this.passwordResetControls.password)
      .pipe(
        catchError((error) => {
          const status = error.status;
          if (status === 400) {
            let modalTitle = 'Invalid token';
            let modalMessage = 'The reset password token is no longer valid.<br> You may wish to restart resetting your password.';

            switch (error?.error?.status) {
              case 'TokenExpired':
                modalTitle = 'Expired Link';
                modalMessage = 'The reset password link has expired.<br> You may wish to restart resetting your password.';
                break;
              case 'TokenSpent':
                modalTitle = 'Link already used';
                modalMessage = 'The reset password link has already been used.<br> You may wish to restart resetting your password.';
                break;
            }

            this.messagingService.openAlertDialog({
              title: modalTitle,
              message: modalMessage
            });
            return of(null);
          } else if (status === 500) {
            this.messagingService.warn(
              'There was an error processing the request.\n\nPlease try again later or contact your administrator.'
            );
            return of(null);
          }
          return throwError(() => error);
        })
      )
      .subscribe((_) => {
        this.router.navigateByUrl(authenticationRoutes.signIn).then();
      });
  }

  cancel() {
    this.router.navigate(['/auth', { outlets: { top: ['sign-in'] } }]).then();
  }
}
